import React, { useId, useMemo, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import { useDeviceEnrichment } from '../hooks/useDeviceEnrichment';
import { groupDevicePhotos, installedPhrase, PhotoEntry } from '../utils/devicePhotos';
import { Device } from '../types';
import { buttonClass } from './ui/Button';
import { LabelPhotoOpener, fileLinkTitle } from './LabelPhotoViewer';

interface DevicePhotosPanelProps {
  device: Device;
}

const LOOKING_FOR_EARLIER = 'Looking for earlier filter records…';
const EARLIER_FAILED =
  'Earlier filter records could not be loaded, so photos of filters before the current one are not shown.';
/** A refresh failed after an earlier read worked: what is on screen is that read. */
const EARLIER_STALE =
  'Earlier filter records could not be refreshed, so they are shown as they were last loaded.';

/** How many earlier filter records have nothing to show, in one plain line. */
const earlierWithoutPhotos = (n: number): string =>
  n === 1 ? '1 earlier filter record has no photos.' : `${n} earlier filter records have no photos.`;

/**
 * The file name out of a photo URL. A Firebase download URL carries the whole
 * storage path as one encoded segment (`o/ai-analysis%2F<uid>%2Ffilter-1.jpg`),
 * so the segment is decoded before the name is taken.
 */
const photoFileName = (url: string): string => {
  const last = url.split(/[?#]/)[0].split('/').filter(Boolean).pop() ?? '';
  let decoded = last;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    // a malformed escape: keep the segment as stored
  }
  return decoded.split('/').filter(Boolean).pop() || url;
};

const isHeic = (url: string): boolean => /\.(heic|heif)$/i.test(photoFileName(url));

/**
 * FS-725: what a tile says when its photo will not draw. The browser only
 * reports that the image failed, not why, so the one cause named is the one
 * the URL itself shows — a HEIC, which Chrome and Firefox cannot draw. Any
 * other failure is described as what it is: the photo did not load. The ones
 * seen so far (devices 222, 275, 281) were stored links to objects that had
 * been deleted from Firebase Storage, which answers a download with 403.
 */
const PhotoUnavailable: React.FC<{ photo: PhotoEntry; failedUrl: string }> = ({ photo, failedUrl }) => {
  const heic = isHeic(failedUrl);
  return (
    <div
      data-testid="photo-unavailable"
      className="flex h-64 flex-col items-center justify-center gap-1 bg-white p-4 text-center"
    >
      <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">
        {heic ? 'This browser can’t preview this photo' : 'Unable to load this photo'}
      </p>
      <p className="text-xs text-muted-foreground">
        {heic
          ? 'iPhone HEIC photo — browsers can’t display this format.'
          : 'It could not be loaded from storage. The file may have been removed.'}
      </p>
      <p className="w-full truncate text-xs text-muted-foreground" title={photoFileName(photo.url)}>
        {photoFileName(photo.url)}
      </p>
      <a
        href={photo.url}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass('link', 'md')}
        title="Open the photo as it was uploaded, in a new tab"
      >
        Open original
      </a>
    </div>
  );
};

/** One photo, drawn the way the Photos tab has always drawn it. */
const PhotoCard: React.FC<{ photo: PhotoEntry }> = ({ photo }) => {
  // A browser cannot draw a HEIC. When the server has made a JPEG of
  // it (a preview, or the label read's copy), that is what is shown and
  // opened — the original stays one click away, because it is the file
  // that was filed.
  const shown = photo.previewUrl ?? photo.url;
  // Keyed to the URL that failed, so a new URL for this tile gets its own try.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const failed = failedUrl === shown;
  return (
    <div className="border rounded-md overflow-hidden bg-gray-50">
      {failed ? (
        <PhotoUnavailable photo={photo} failedUrl={shown} />
      ) : (
        <LabelPhotoOpener
          src={shown}
          alt={photo.label}
          degrees={photo.rotation}
          boxClassName="w-full h-64 bg-white"
          fit="contain"
          onError={() => setFailedUrl(shown)}
          label={`Show this photo large: ${photo.label}`}
          heading={photo.label}
          fileTitle={fileLinkTitle(shown !== photo.url)}
          className="block w-full rounded-none focus-visible:ring-inset focus-visible:ring-offset-0"
        />
      )}
      <div className="p-3 border-t">
        <p className="text-sm font-medium">{photo.label}</p>
        <p className="text-xs text-muted-foreground">{photo.source}</p>
        {/* Whenever the tile draws a copy, the upload itself stays one click away. */}
        {shown !== photo.url && (
          <p className="text-xs text-muted-foreground mt-1">
            <span>
              {photo.convertedFrom ? `${photo.convertedFrom.toUpperCase()} · converted` : 'converted'}
            </span>{' '}
            <a
              href={photo.url}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClass('link', 'sm')}
              title="Open the photo as it was uploaded"
            >
              original
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

const PhotoGrid: React.FC<{ photos: PhotoEntry[] }> = ({ photos }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {photos.map((photo, idx) => (
      <PhotoCard key={`${photo.url}-${idx}`} photo={photo} />
    ))}
  </div>
);

/** One titled group of photos. The heading also names the region for a screen reader. */
const PhotoSection: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="py-5 first:pt-0">
      <div className="mb-3">
        <h4 id={headingId} className="text-base font-semibold text-gray-800">
          {title}
        </h4>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
};

/**
 * Plan 94 Phase 2 - route-bound Device Photos body.
 *
 * Renders the MyFluxHive app photos for one device's Firestore enrichment, with
 * no selector and no cross-page pin. The legacy `/photos` route wraps this with a
 * selector + `?device=` sync until Phase 6 redirects it into the workspace.
 *
 * FS-539 — grouped by record: the HVAC and A/C equipment, the current filter,
 * then each earlier filter under its own install date. A recorded filter
 * change used to take the old filter's photos out of view, because every read
 * followed the device's current filter; the single-device read now returns
 * the earlier records too.
 */
const DevicePhotosPanel: React.FC<DevicePhotosPanelProps> = ({ device }) => {
  const { enrichmentMap, loading, error } = useFirestoreEnrichment();
  const detail = useDeviceEnrichment(device.name);
  // The batch map is on hand at once and is what every other tab shows, so it
  // stays the source of the current records. The single-device read adds the
  // earlier filters when it lands, and stands in for the batch only when the
  // batch has nothing for this device.
  const enrichment = enrichmentMap[device.name] ?? detail.enrichment ?? undefined;
  const previousFilters = detail.enrichment?.previousFilters;
  const groups = useMemo(
    () => groupDevicePhotos(enrichment, previousFilters),
    [enrichment, previousFilters],
  );
  const { equipment, currentFilter, previousWithoutPhotos, total } = groups;

  const photolessNote = previousWithoutPhotos > 0 && (
    <p className="mt-3 text-sm text-gray-600">{earlierWithoutPhotos(previousWithoutPhotos)}</p>
  );
  const failedNote = detail.error && (
    <div role="alert" className="mt-3 text-sm text-status-error-foreground">
      <p>Photo history error</p>
      <p>{previousFilters ? EARLIER_STALE : EARLIER_FAILED}</p>
    </div>
  );

  return (
    <div>
      {loading && <p className="text-muted-foreground mb-4">Loading app data…</p>}

      {error && (
        <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mb-4">Could not load photos: {error}</div>
      )}
      {detail.error && !enrichment && !loading && (
        <p role="alert" className="mb-4 text-destructive">Could not load this device's photo records.</p>
      )}

      {!loading && !error && !detail.error && !enrichment && !detail.loading && (
        <div className="border border-status-neutral-border bg-status-neutral text-status-neutral-foreground p-4 rounded-md">
          No Firestore app data for this device. Photos are only available for devices
          registered through the MyFluxHive app.
        </div>
      )}

      {enrichment && total === 0 && (
        <div>
          {detail.error ? failedNote : detail.loading ? (
            // An earlier filter may still turn up with photos; saying there
            // are none before the read lands would be wrong half the time.
            <p className="text-muted-foreground">{LOOKING_FOR_EARLIER}</p>
          ) : (
            <div className="border border-status-warning-border bg-status-warning text-status-warning-foreground p-4 rounded-md">
              No photos have been uploaded for this device yet.
            </div>
          )}
          {photolessNote}
        </div>
      )}

      {total > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">
            {total} photo{total !== 1 ? 's' : ''}
          </h3>
          <div className="divide-y divide-gray-200">
            {equipment.length > 0 && (
              <PhotoSection title="HVAC and A/C equipment">
                <PhotoGrid photos={equipment} />
              </PhotoSection>
            )}

            <PhotoSection
              title={
                currentFilter
                  ? `Current filter — ${installedPhrase(currentFilter.installDay)}`
                  : 'Current filter'
              }
              subtitle={currentFilter?.identity || undefined}
            >
              {!currentFilter ? (
                <p className="text-sm text-muted-foreground">This device has no current filter record.</p>
              ) : currentFilter.photos.length > 0 ? (
                <PhotoGrid photos={currentFilter.photos} />
              ) : (
                <p className="text-sm text-muted-foreground">No photos on the current filter record.</p>
              )}
              {photolessNote}
              {detail.loading && <p className="mt-3 text-sm text-muted-foreground">{LOOKING_FOR_EARLIER}</p>}
              {failedNote}
            </PhotoSection>

            {groups.previousFilters.map((section, idx) => (
              <PhotoSection
                key={section.id ?? `previous-${idx}`}
                title={`Previous filter — ${installedPhrase(section.installDay)}`}
                subtitle={section.identity || undefined}
              >
                <PhotoGrid photos={section.photos} />
              </PhotoSection>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicePhotosPanel;
